// Angular imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

// 3rd party imports
import { formatNumber } from 'devextreme/localization';
import notify from 'devextreme/ui/notify';

// Constant imports
import { IconConstants, Utils } from 'cc-common';
import { Constants } from '../../../../_constants/constants';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';

// Service imports
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { ARPaymentService } from '../../_services/account-receivable/ar-payment.service';
import { ARCreditApplicationService } from '../../_services/account-receivable/ar-credit-application.service';
import { UserService } from '../../../../_services/data-services/user.service';

// Model imports
import { FileInfoDisplayResultDto } from '../../../../_models/file-info-display-result-dto';
import { ARPaymentParameterDto } from '../../_models/account-receivable/ar-payment/ar-payment-parameter-dto';
import { ARPaymentResultDto } from '../../_models/account-receivable/ar-payment/ar-payment-result-dto';
import { ARCreditApplicationsResultDto } from '../../_models/account-receivable/credit-applications/ar-credit-applications-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';

@Component({
    selector: 'app-ar-payment-details',
    templateUrl: './ar-payment-details.component.html',
    styleUrls: ['./ar-payment-details.component.scss']
})
export class ARPaymentDetailsComponent implements OnInit {
    @Input() visible: boolean;
    @Input() arPaymentParam: ARPaymentParameterDto;
    @Output() onARPaymentClose = new EventEmitter<boolean>();

    isInternalUser = false;
    loadingVisible = false;
    dateFormat = StringFormatConstants.dateFormat;
    currencyFormat = StringFormatConstants.currencyFormat;
    defaultLabelLocation = Constants.defaultFormLabelLocation;

    historyIcon = IconConstants.HISTORY;
    attachmentsIcon = IconConstants.ATTACHMENT;
    editIcon = IconConstants.EDIT;
    deleteIcon = IconConstants.DELETE;
    closeIcon = IconConstants.CLOSE;
    iconReverse = IconConstants.REVERSE;
    iconExchange = IconConstants.EXCHANGE;

    popUpTitle: string;
    arPaymentDetail: ARPaymentResultDto;

    showTransferHistory = false;
    showAttachments = false;
    showCreditApplication = false;
    showManualCreditApplication = false;
    showReverseEditor = false;
    showPaymentEditor = false;
    showPaymentTransferEditor = false;
    showIssueRefundEditor = false;
    displayTransactionLogPopUp = false;

    transactionLogTitle: string;
    communityId: number;
    transactionLogResultData: TransactionLogResultDto[] = [];
    transactionType = 'Payment';

    creditApplicationData: ARCreditApplicationsResultDto[] = [];

    isForceRefreshRequired = false;

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService,
        private readonly arPaymentService: ARPaymentService,
        private readonly arCreditApplicationService: ARCreditApplicationService,
        private readonly userService: UserService
    ) {
        this.isInternalUser = this.userService.isInternal();

        this.currencyFormat = this.precisionService.updatePrecisionPoint(this.currencyFormat);
    }

    ngOnInit() {
        if (!!this.arPaymentParam) {
            this.popUpTitle = 'AR Payment ' + this.arPaymentParam.paymentId;
            this.populatePaymentDetail(false);
        }
    }

    onShown(e: any): void {
        e.component.repaint();
    }

    onHidden(): void {
        this.visible = false;
        this.onARPaymentClose.emit(this.isForceRefreshRequired);
    }

    showPaymentTransferHistory(): void {
        this.showTransferHistory = true;
    }

    showARCreditApplication(): void {
        this.loadingVisible = true;
        this.arCreditApplicationService.getARPaymentCreditApplications(this.arPaymentParam).subscribe(
            (result) => {
                this.creditApplicationData = result;
                this.showCreditApplication = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    showARManualCreditApplication(): void {
        this.showManualCreditApplication = true;
    }

    closeCreditApplication(unapply): void {
        this.showCreditApplication = false;
        if (unapply) {
            this.isForceRefreshRequired = true;
            this.populatePaymentDetail(true);
        }
    }

    navigateToProperty(): void {
        this.helperService.openDefaultPropertyPageInNewWindow(
            this.arPaymentDetail.communityId,
            this.arPaymentDetail.customerCode
        );
    }

    showAttachmentList(): void {
        if (this.arPaymentDetail.attachments.length > 1) {
            this.showAttachments = true;
        } else if (this.arPaymentDetail.attachments.length > 0) {
            const file: FileInfoDisplayResultDto = this.arPaymentDetail.attachments[0];
            this.helperService.downloadFileUsingUrl(file.downloadUrl, file.fileName);
        }
    }

    showTransactionHistory(): void {
        this.loadingVisible = true;
        this.arPaymentService
            .getARPaymentTransactionHistory(this.arPaymentParam.paymentId, this.arPaymentParam.clearCache)
            .subscribe(
                (result) => {
                    this.transactionLogResultData = result;
                    this.communityId = this.arPaymentParam.communityId;
                    this.transactionLogTitle = `Log of AR Payment ${this.arPaymentParam.paymentId}`;
                    this.displayTransactionLogPopUp = true;
                    this.loadingVisible = false;
                },
                (error) => {
                    this.loadingVisible = false;
                    this.errorService.displayError(error);
                }
            );
    }

    getFileIconByExtension(file: FileInfoDisplayResultDto): string {
        if (file && file.fileName) {
            return this.helperService.getFileIconByExtension(file.fileName);
        }
        return '';
    }

    downloadSupportingDocument(file: FileInfoDisplayResultDto): void {
        this.helperService.downloadFileUsingUrl(file.downloadUrl, file.fileName);
    }

    currencyFormatter(value: number): string {
        return formatNumber(value, this.currencyFormat);
    }

    onReverseClick(): void {
        this.showReverseEditor = true;
    }

    onEditClick(): void {
        this.showPaymentEditor = true;
    }

    onTransferClick(): void {
        this.showPaymentTransferEditor = true;
    }

    onPaymentTransferEditorClose(forceReload: boolean): void {
        this.showPaymentTransferEditor = false;
        if (Utils.isTrue(forceReload)) {
            this.isForceRefreshRequired = true;
            this.populatePaymentDetail(true);
        }
    }

    onIssueRefundClick(): void {
        this.showIssueRefundEditor = true;
    }

    onIssueRefundPaymentEditorClose(forceReload: boolean): void {
        this.showIssueRefundEditor = false;

        if (Utils.isTrue(forceReload)) {
            this.isForceRefreshRequired = true;
            this.populatePaymentDetail(true);
        }
    }

    downloadPaymentCheckImage(): void {
        this.loadingVisible = true;
        // below method will also download the file
        this.arPaymentService.getARPaymentCheckImage(this.arPaymentParam).subscribe(
            (result: Blob) => {
                this.helperService.showBlobInBrowserTab(
                    result,
                    `${this.arPaymentDetail.paymentId}_${this.arPaymentDetail.paymenType}.png`
                );
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    onManualCAEditorClose(forceReload: boolean): void {
        this.showManualCreditApplication = false;
        if (Utils.isTrue(forceReload)) {
            this.isForceRefreshRequired = true;
            this.populatePaymentDetail(true);
        }
    }

    onDeleteClick(): void {
        this.loadingVisible = true;
        this.arPaymentService.deleteARPayment(this.arPaymentParam).subscribe(
            () => {
                notify('AR Payment deleted Successfully.', 'success', MessageInterval.successMsgInterval);
                this.isForceRefreshRequired = true;
                this.onHidden();
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    onARPaymentEditorClose(forceReload: boolean): void {
        this.showPaymentEditor = false;

        if (Utils.isTrue(forceReload)) {
            this.isForceRefreshRequired = true;
            this.populatePaymentDetail(true);
        }
    }

    private populatePaymentDetail(clearCache: boolean): void {
        this.loadingVisible = true;
        this.arPaymentParam.clearCache = clearCache;
        this.arPaymentService.getARPaymentDetail(this.arPaymentParam).subscribe(
            (result) => {
                this.arPaymentDetail = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
                this.isForceRefreshRequired = false;
                this.onHidden();
            }
        );
    }
}
